import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class Parallax implements Plugin {
  private observer: IntersectionObserver | null = null;
  private isInView = false;
  private parallaxSpeed: number;
  private image: HTMLImageElement | null = null;
  private animationFrame: number | null = null;
  private isReducedMotion: boolean = false;

  constructor(private element: HTMLElement) {
    this.parallaxSpeed = parseFloat(element.dataset.parallaxSpeed || "0.5");
    this.image = element.querySelector(
      "[data-parallax-image]",
    ) as HTMLImageElement;

    this.isReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
  }

  init(): void {
    if (!this.image) {
      console.warn(
        "Parallax: No image found with data-parallax-image attribute",
      );
      return;
    }

    if (this.isReducedMotion) {
      return;
    }

    this.isInView = true;
    this.setupScrollHandler();

    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isInView = entry.isIntersecting;
        });
      },
      {
        rootMargin: "50px 0px",
        threshold: 0,
      },
    );

    this.observer.observe(this.element);
  }

  private setupScrollHandler(): void {
    const handleScroll = () => {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
      if (this.image) {
        this.animationFrame = requestAnimationFrame(() => {
          this.updateParallax();
          this.animationFrame = null;
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
  }

  private updateParallax(): void {
    if (!this.image) {
      return;
    }

    const scrollY = window.scrollY;
    const translateY = scrollY * this.parallaxSpeed;

    this.image.style.transform = `translateY(${translateY}px) scale(1.1)`;
  }

  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (this.image) {
      this.image.style.transform = "";
    }
  }
}

PluginRegistry.register("parallax", Parallax);
